/**
 * Box 4 of the ICS-201: the map or sketch of the incident.
 *
 * The box owns a map of its own rather than borrowing the main one. Capturing
 * from the global map always reframed to the incident marker and printed
 * whatever layers happened to be switched on elsewhere, so the sketch was never
 * really the user's to compose. Here they pan, zoom, and pick the layers the
 * printed form should carry; feature editing happens on the primary Map Layers
 * map. The snapshot button saves exactly the framed Box 4 view.
 *
 * The PNG is taken at the live view's width and height so the saved sketch and
 * the PDF Map field show the same extent the user framed, rather than a square
 * crop. `drawImageOnPDF` letterboxes whatever aspect ratio arrives.
 */

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as reactiveUtil from '@arcgis/core/core/reactiveUtils';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import { toast } from 'react-toastify';
import { useSearchParams } from 'react-router';

import { Button } from '@/components/ui/button';
import { uploadFilesToBackend } from 'components/FileManagement/hooks/fileMutations';
import { useSignedUrl } from 'components/FileManagement/hooks/useNotebookQueries';
import {
  useActualIncidentId,
  useGroupGuid,
} from 'components/IAP/hooks/commonHooks';
import { useIncident } from 'components/IAP/hooks/incidentHooks';
import {
  useIncidentInfo,
  useUpdateIcsInfo,
} from 'components/IAP/hooks/incidentInfoHooks';
import { CorporateViewTabs } from 'components/MapPage/hubNavTabs';
import { HYBRID } from 'components/MapPage/CorporateViewTabs/LayersTab/arcgis-integration/arcGisTypes';
import { US_INITIAL_VIEW } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/constants';
import { useExternalLayerSync } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/useExternalLayerSync';
import { useInternalLayerSync } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/useInternalLayerSync';
import { useLocalArcgisMapView } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/useLocalArcgisMapView';
import { useLocationGraphicLayer } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/useLocationGraphicLayer';
import { useUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import { useListExternalLayersQuery } from 'hooks/externalLayer/useListExternalLayersQuery';

import { useRecordIcs201Version } from '../ics201VersionRecording';

import { ics201SketchScreenshotSize } from './ics201MapSketch';
import {
  Ics201MapLayerPicker,
  usePickableLayers,
  type PickableLayer,
} from './Ics201MapLayerPicker';

/**
 * Close enough to read a scene without hunting for it, once the incident has a
 * location.
 */
const INCIDENT_ZOOM = 12;

export type Ics201MapSketchHandle = {
  /** Screenshot of the framed Box 4 view, or undefined if the map is not ready. */
  captureSketchFile: () => Promise<File | undefined>;
};

export const Ics201MapSketchBox = forwardRef<
  Ics201MapSketchHandle,
  { disabled?: boolean }
>(function Ics201MapSketchBox({ disabled = false }, ref) {
  const recordVersion = useRecordIcs201Version();
  const incidentId = useActualIncidentId();
  const groupGuid = useGroupGuid();
  const incident = useIncident();
  const { data: incidentInfo } = useIncidentInfo();
  const incidentMutation = useUpdateIcsInfo();
  const { mutateAsync: getSignedUrl } = useSignedUrl();
  const { setIsMapVisible } = useUscgMapContext();
  const [, setSearchParams] = useSearchParams();

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>();

  const mapSketchUrl = incidentInfo?.mapSketchUrl;
  const incidentLocation = incident.data?.location;
  const incidentAddress = incident.data?.address;

  // ─── The map ────────────────────────────────────────────────────────────────

  const { mapView } = useLocalArcgisMapView({
    isActive: true,
    containerRef: mapContainerRef,
    basemap: HYBRID,
    initialView: US_INITIAL_VIEW,
  });

  /**
   * The incident marker sits on its own layer so layer toggles and snapshots
   * always include the pin the form is about.
   */
  const locationLayerRef = useRef<GraphicsLayer>(
    new GraphicsLayer({ title: 'ICS-201 incident location' })
  );
  const clearLocationLayer = useCallback(() => {
    locationLayerRef.current.removeAll();
  }, []);

  const { drawLocationOnMap } = useLocationGraphicLayer({
    mapView,
    drawingLayerRef: locationLayerRef,
    clearDrawingLayer: clearLocationLayer,
  });

  useEffect(() => {
    // Held rather than re-read on the way out. `useLocalArcgisMapView` owns this view, and because its hook is called
    // first its cleanup runs first, destroying the view and nulling `mapView.map` before this cleanup is reached.
    const map = mapView?.map;
    if (!map) return;
    const layer = locationLayerRef.current;
    map.add(layer);
    return () => {
      // Nothing to detach from once the map is torn down, and reaching into it throws.
      if (!(map as unknown as { destroyed?: boolean }).destroyed) {
        map.remove(layer);
      }
    };
  }, [mapView]);

  // Opening on the incident rather than on the whole country, so the first thing shown is worth printing.
  const hasFramedRef = useRef(false);
  useEffect(() => {
    if (!mapView || !incidentLocation) return;
    drawLocationOnMap(incidentLocation, incidentAddress ?? undefined, mapView);
    if (hasFramedRef.current) return;
    hasFramedRef.current = true;
    void mapView.goTo({
      target: locationLayerRef.current.graphics.toArray(),
      zoom: INCIDENT_ZOOM,
    });
  }, [mapView, incidentLocation, incidentAddress, drawLocationOnMap]);

  // ─── Layers ─────────────────────────────────────────────────────────────────

  const {
    groups: pickableGroups,
    layers: pickableLayers,
    isLoading: isLoadingLayers,
  } = usePickableLayers(incidentId);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(
    () => new Set<string>()
  );

  /**
   * Visibility is held here rather than read from `UscgMapContext`, which is
   * the whole point of the picker: turning a layer on for the printed sketch
   * must not change what the main map is showing.
   */
  const [externalVisibility, setExternalVisibility] = useState<
    Map<string, boolean | null>
  >(() => new Map());
  const [internalVisibility, setInternalVisibility] = useState<
    Map<string, boolean | null>
  >(() => new Map());
  const addedExternalLayersRef = useRef<Map<string, __esri.Layer[]>>(new Map());
  const internalLayersRef = useRef<Map<string, GraphicsLayer>>(new Map());

  const toggleLayer = useCallback((layer: PickableLayer) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const nowOn = !next.has(layer.id);
      if (nowOn) next.add(layer.id);
      else next.delete(layer.id);

      const setVisibility =
        layer.kind === 'external'
          ? setExternalVisibility
          : setInternalVisibility;
      setVisibility((current) => new Map(current).set(layer.id, nowOn));

      return next;
    });
  }, []);

  // Catalog rows carry only a name; the sync needs the service and auth details, which live on the layer records.
  const { data: externalLayerPages } = useListExternalLayersQuery(
    { filter: { groupGuid: groupGuid ?? '' } },
    { enabled: !!groupGuid }
  );

  const externalLayersToSync = useMemo(
    () =>
      (externalLayerPages?.data ?? []).filter((layer) =>
        selectedIds.has(String(layer.id))
      ),
    [externalLayerPages, selectedIds]
  );

  const internalIncludeIds = useMemo(() => {
    const internalIds = new Set(
      pickableLayers.filter((l) => l.kind === 'internal').map((l) => l.id)
    );
    return new Set([...selectedIds].filter((id) => internalIds.has(id)));
  }, [pickableLayers, selectedIds]);

  useExternalLayerSync({
    mapView,
    externalLayers: externalLayersToSync,
    externalLayerVisibilityById: externalVisibility,
    setExternalLayerVisibilityById: setExternalVisibility,
    addedExternalLayersRef,
  });

  useInternalLayerSync({
    mapView,
    internalLayerVisibilityById: internalVisibility,
    incidentFilterId: incidentId ?? null,
    // No sidebar here to choose a version with, so each layer draws its draft, which is what the main map defaults to.
    selectedInternalLayerVersionByInternalLayerId: {},
    layersRef: internalLayersRef,
    includeLayerIds: internalIncludeIds,
  });

  // ─── Capture ────────────────────────────────────────────────────────────────

  const captureSketchFile = useCallback(async (): Promise<File | undefined> => {
    if (!mapView) return undefined;
    await mapView.when();
    // A frame grabbed mid-pan or before the tiles land comes out half drawn.
    await reactiveUtil.whenOnce(() => mapView.stationary && !mapView.updating);

    const size = ics201SketchScreenshotSize(mapView);
    if (!size) return undefined;

    const screenshot = await mapView.takeScreenshot({
      format: 'png',
      ...size,
    });
    const blob = await (await fetch(screenshot.dataUrl)).blob();
    return new File([blob], 'mapSketch.png', { type: 'image/png' });
  }, [mapView]);

  useImperativeHandle(ref, () => ({ captureSketchFile }), [captureSketchFile]);

  const saveView = async () => {
    setIsSaving(true);
    try {
      const file = await captureSketchFile();
      if (!file) {
        toast.error('The map is not ready to capture. Retry in a moment.');
        return;
      }

      const [uploaded] = await uploadFilesToBackend({
        files: [file],
        workspaceId: incidentId,
        // A form field image, not a document anyone should retrieve by search.
        ragIndex: false,
      });

      if (!uploaded?.fileUrl) {
        toast.error('The sketch did not upload. Retry in a moment.');
        return;
      }

      incidentMutation.mutate(
        {
          ...incidentInfo,
          mapSketchUrl: uploaded.fileUrl,
        } as Parameters<typeof incidentMutation.mutate>[0],
        { onSuccess: () => recordVersion() }
      );
    } catch (error) {
      console.error('Could not save the ICS-201 map sketch:', error);
      toast.error('The sketch could not be saved.');
    } finally {
      setIsSaving(false);
    }
  };

  const goToPrimaryMap = () => {
    setIsMapVisible(true);
    setSearchParams(
      (params) => {
        params.set('openTab', CorporateViewTabs.Layers);
        return params;
      },
      { replace: true }
    );
  };

  // ─── The saved sketch ───────────────────────────────────────────────────────

  // The stored value is a blob path, which only renders once signed.
  useEffect(() => {
    let cancelled = false;
    if (!mapSketchUrl) {
      setPreviewUrl(undefined);
      return;
    }
    (async () => {
      try {
        const signed = await getSignedUrl(mapSketchUrl);
        if (!cancelled) setPreviewUrl(signed);
      } catch {
        if (!cancelled) setPreviewUrl(undefined);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mapSketchUrl, getSignedUrl]);

  return (
    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3">
      <div className="tw:grid tw:min-w-0 tw:gap-3 tw:lg:grid-cols-[260px_1fr]">
        <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-2">
          <span className="tw:text-sm tw:font-medium">
            Layers on the sketch
          </span>
          <Ics201MapLayerPicker
            groups={pickableGroups}
            isLoading={isLoadingLayers}
            selectedIds={selectedIds}
            onToggle={toggleLayer}
            disabled={disabled}
          />
        </div>

        <div
          className="tw:relative tw:h-80 tw:w-full tw:min-w-0 tw:overflow-hidden tw:rounded tw:border tw:border-[var(--border-primary)]"
          data-testid="ics201-map-sketch-map"
        >
          <div ref={mapContainerRef} className="tw:h-full tw:w-full" />
        </div>
      </div>

      {disabled ? null : (
        <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-end tw:gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={goToPrimaryMap}
            data-tour="ics201-go-primary-map"
            data-testid="ics201-go-primary-map"
          >
            Go to Primary Map
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => void saveView()}
            disabled={isSaving}
            data-testid="ics201-save-map-sketch"
            data-tour="ics201-map-snapshot"
          >
            {isSaving ? 'Saving...' : 'Take Snapshot for ICS-201 Export'}
          </Button>
        </div>
      )}

      {previewUrl ? (
        <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-1">
          <span className="tw:text-sm tw:font-medium">Saved sketch</span>
          <img
            src={previewUrl}
            alt="Saved map sketch for the incident"
            className="tw:max-h-60 tw:w-full tw:rounded tw:border tw:border-[var(--border-primary)] tw:object-contain"
            data-testid="ics201-map-sketch-preview"
          />
        </div>
      ) : (
        <p className="tw:text-sm tw:text-muted-foreground">
          No sketch saved yet. Compose layers and frame the map above, then take
          a snapshot to fill Box 4. Use Go to Primary Map for Map Layers
          feature editing.
        </p>
      )}
    </div>
  );
});

Ics201MapSketchBox.displayName = 'Ics201MapSketchBox';

export default Ics201MapSketchBox;
